import React from 'react';
import { Box, Flex } from '@rebass/grid';
import moment from 'moment-timezone';
import * as Yup from 'yup';
import FormikField from 'components/CreateIncident/components/FormikField';
import { getBorderDates } from '../components/CreateIncident/constant';
import i18n from '../i18n';

export const FORMAT = 'YYYY-MM-DD hh:mm A';
export const createFormTreeFromConfig = (config, formikProps) => (
  <>
    <Flex justifyContent="space-between" mb={3}>
      <div style={{ fontWeight: 500 }}>{config.formTitle.value}</div>
      <FormikField {...config.privacy} {...formikProps} />
    </Flex>
    <div>
      <Box mb={3}>
        <FormikField {...config.name} {...formikProps} />
      </Box>
      <Box mb="20px">
        <FormikField {...config.description} {...formikProps} />
      </Box>
      <Flex flexDirection="column" justifyContent="flex-end" mb="20px">
        <FormikField {...config.source} {...formikProps} />
        <Flex justifyContent="flex-end" mt="2">
          <FormikField {...config.backfill} {...formikProps} />
        </Flex>
      </Flex>
      <Flex mb="20px" width={1} flexDirection="column">
        <FormikField {...config['tz-info']} {...formikProps} />
        <Flex>
          <FormikField {...config.start} {...formikProps} />
          <FormikField {...config.end} {...formikProps} />
        </Flex>
        <FormikField {...config.duration} {...formikProps} />
      </Flex>
      <FormikField {...config.affected} {...formikProps} />
      <FormikField {...config.automation} {...formikProps} />
      <FormikField
        config={config.notification_options}
        type="notification_options"
        formikProps={formikProps}
      />
    </div>
  </>
);

export function getValidationDate(date, timezone) {
  return moment.tz(date, timezone).format(moment.HTML5_FMT.DATETIME_LOCAL);
}

export const getValidationSchema = (type, timezone, edit, startValue) => {
  const { startMin, startMax, completeMin, completeMax } = getBorderDates(
    type,
    timezone,
    edit,
    startValue
  );
  return Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(3, i18n.t('helpers.forms.validationSchema.minTitle'))
      .max(280, i18n.t('helpers.forms.validationSchema.maxTitle'))
      .required(i18n.t('helpers.forms.validationSchema.requiredTitle')),
    description: Yup.string()
      .min(3, i18n.t('helpers.forms.validationSchema.minDescription'))
      .trim()
      .max(5000, i18n.t('helpers.forms.validationSchema.maxDescription'))
      .required(i18n.t('helpers.forms.validationSchema.requiredDescription')),
    // ...(!isCancelledSM && {
    start: Yup.date()
      // .default(moment(min).toDate())
      .min(
        startMin,
        i18n.t('helpers.forms.validationSchema.startTimeLaterThan', {
          date: moment(startMin).format(FORMAT)
        })
      )
      .max(
        startMax,
        i18n.t('helpers.forms.validationSchema.startTimeEarlierThan', {
          date: moment(startMax).format(FORMAT)
        })
      )
      .required(i18n.t('helpers.forms.validationSchema.requiredStartDate')),
    ...(type === 'planned' && {
      end:
        type === 'planned'
          ? Yup.date()
              // .default(moment(max).toDate())
              .min(
                completeMin,
                i18n.t('helpers.forms.validationSchema.endTimeLaterThan', {
                  date: moment(completeMin).format(FORMAT)
                })
              )
              .max(
                moment(completeMax).toDate(),
                i18n.t('helpers.forms.validationSchema.endTimeEarlierThan', {
                  date: moment(completeMax).format(FORMAT)
                })
              )
              .required(
                i18n.t('helpers.forms.validationSchema.requiredEndDate')
              )
          : Yup.date()
    })
  });
};

export const getIncidentUpdatesValidationSchema = (
  timezone,
  minTime,
  isFirstUpdate,
  isMaintenance
) => {
  const max = moment
    .tz(
      moment(new Date())
        .seconds(59)
        .toDate(),
      timezone
    )
    .format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS);
  const min = getValidationDate(moment(new Date(minTime)).toDate(), timezone);
  return Yup.object().shape({
    message: Yup.string()
      .trim()
      .min(
        3,
        i18n.t('helpers.forms.incidentUpdatesValidationSchema.minMessageChar')
      )
      .max(
        5000,
        i18n.t('helpers.forms.incidentUpdatesValidationSchema.maxMessageChar')
      )
      .required(
        i18n.t('helpers.forms.incidentUpdatesValidationSchema.requiredMessage')
      ),
    display_time: Yup.date()
      .min(
        min,
        `* Update time should be greater than or equal to the ${
          isFirstUpdate
            ? `${isMaintenance ? 'maintenance created' : 'incident opened'}`
            : 'previous update'
        } time ${moment(min).format(FORMAT)}`
      )
      .max(
        new Date(max),
        i18n.t(
          'helpers.forms.incidentUpdatesValidationSchema.updateTimeLessThanCurrent',
          { date: moment(max).format(FORMAT) }
        )
      )
      .required(
        i18n.t(
          'helpers.forms.incidentUpdatesValidationSchema.requiredIncidentUpdateTime'
        )
      )
  });
};

export const getEditIncidentUpdatesValidationSchema = (
  timezone,
  minTime,
  maxTime,
  updateStatus,
  isFirst,
  isLast
) => {
  const max = maxTime
    ? getValidationDate(
        moment(new Date(maxTime))
          .seconds(0)
          .milliseconds(0)
          .toDate(),
        timezone
      )
    : getValidationDate(moment(new Date()).toDate(), timezone);
  const min = getValidationDate(
    moment(new Date(minTime))
      .seconds(0)
      .milliseconds(0)
      .toDate(),
    timezone
  );
  return Yup.object().shape({
    message: Yup.string()
      .trim()
      .min(
        3,
        i18n.t('helpers.forms.incidentUpdatesValidationSchema.minMessageChar')
      )
      .max(
        5000,
        i18n.t('helpers.forms.incidentUpdatesValidationSchema.maxMessageChar')
      )
      .required(
        i18n.t('helpers.forms.incidentUpdatesValidationSchema.requiredMessage')
      ),
    editDate: Yup.date()
      .min(
        min,
        `* Update time should be greater than the ${
          isFirst ? 'incident created' : 'previous update'
        } time ${moment(min).format(FORMAT)}`
      )
      .max(
        max,
        `* Update time should be lesser than or equal to the ${
          isLast
            ? updateStatus === 'CL'
              ? 'incident end time'
              : 'current time'
            : 'next update'
        } ${moment(max).format(FORMAT)}`
      )
  });
};
